'use client'

import { RandomProjectTokenFragment, RandomWelcomeProjectFragment } from '@/gql/generated/graphql'
import Image from 'next/image'
import React from 'react'
import { motion } from 'motion/react'

type ProjectsAnimationProps = {
  projects?: RandomWelcomeProjectFragment[]
  isHovered: boolean
}

const StaticImage = ({ token }: { token?: RandomProjectTokenFragment | null }) => {
  return (
    <Image
      priority
      src={token?.media_url as string}
      width={0}
      height={0}
      blurDataURL={token?.image?.metadata?.blurHash as string}
      sizes="100vw"
      style={{ width: '100%', height: 'auto' }}
      alt={`${token?.id} image`}
      className="image-drop-shadow"
    />
  )
}

export const ProjectsAnimation = ({ projects, isHovered }: ProjectsAnimationProps): React.ReactNode => {
  const cardTransition = {
    duration: 0.35,
    ease: 'easeInOut'
  }
  if (!projects?.length) return null
  return (
    <div className="relative h-full w-full">
      {/* Animation boxes */}
      <div className="hidden md:flex">
        <div className="absolute -top-4 right-20 z-10 flex h-auto w-1/6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? -6 : -12,
              translateX: isHovered ? '-7%' : '20%',
              translateY: isHovered ? '5%' : '-20%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[0].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
        <div className="absolute -right-10 top-7 flex h-auto w-1/6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? -20 : -30,
              translateX: isHovered ? '-5%' : '25%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[1].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
        <div className="absolute -right-10 top-[20%] z-10 flex h-auto w-1/6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? 10 : 25,
              translateX: isHovered ? '-5%' : '20%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[2].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
        <div className="absolute -right-10 top-[40%] flex h-auto w-1/6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? 2 : 10,
              translateX: isHovered ? '-2%' : '30%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[3].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
        <div className="absolute -right-10 top-[60%] z-10 flex h-auto w-1/6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? 4 : -12,
              translateX: isHovered ? '-5%' : '20%',
              translateY: isHovered ? '-5%' : '20%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[4].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
        <div className="absolute -bottom-10 right-20 flex h-auto w-1/6 rotate-6 items-end">
          <motion.div
            animate={{
              rotate: isHovered ? -12 : 6,
              translateX: isHovered ? '-10%' : '20%',
              translateY: isHovered ? '-10%' : '20%'
            }}
            transition={cardTransition}
          >
            <StaticImage token={projects?.[5].featured_token?.[0] as RandomProjectTokenFragment} />
          </motion.div>
        </div>
      </div>
      {/* Static boxes */}
      <div className="flex md:hidden">
        <div className="absolute bottom-[5%] left-0 flex h-auto w-1/4 rotate-45 items-end">
          <StaticImage token={projects?.[0].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
        <div className="absolute bottom-0 left-0 flex h-auto w-1/4 rotate-12 items-end">
          <StaticImage token={projects?.[1].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
        <div className="-bottom-xl absolute left-[25%] h-auto w-1/4 rotate-6">
          <StaticImage token={projects?.[2].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
        <div className="-bottom-xs absolute left-[50%] h-auto w-1/4 -rotate-6">
          <StaticImage token={projects?.[3].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
        <div className="-bottom-sm absolute left-[75%] h-auto w-1/4 -rotate-12">
          <StaticImage token={projects?.[4].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
        <div className="-right-xl absolute bottom-[5%] flex h-auto w-1/4 -rotate-[30deg] items-end">
          <StaticImage token={projects?.[5].featured_token?.[0] as RandomProjectTokenFragment} />
        </div>
      </div>
    </div>
  )
}
